import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getDataWAuth, putDataWAuth } from "../../../utils/api-service";
import {
  EXTENSION_URI,
  EXTENSION_SEND_TO_BP,
  EXTENSION_SAVE_CCM,
  APP_VIEW,
} from "../../../constants/constant";

export const fetchCCMRequests = createAsyncThunk(
  "ccmExtension/fetchCCMRequests",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getDataWAuth(EXTENSION_URI, { view: APP_VIEW.CCM });
      return response.data?.extensionRequestList || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch CCM requests");
    }
  }
);

export const sendToBP = createAsyncThunk(
  "ccmExtension/sendToBP",
  async ({ id, requestStatus }, { rejectWithValue }) => {
    try {
      await putDataWAuth(EXTENSION_SEND_TO_BP, { id, requestStatus });
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to send to BP");
    }
  }
);

export const saveCCMData = createAsyncThunk(
  "ccmExtension/saveCCMData",
  async (data, { rejectWithValue }) => {
    try {
      const response = await putDataWAuth(EXTENSION_SAVE_CCM, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to save CCM data");
    }
  }
);

const initialState = {
  requests: [],
  requestsRef: [],
  loading: false,
  error: null,
  actionLoading: false,
  actionError: null,
};

const ccmExtensionSlice = createSlice({
  name: "ccmExtension",
  initialState,
  reducers: {
    filterCCMRequests: (state, action) => {
      const { country, extensionType, countryCode, otherThanJDWINCountries, referenceContract, serialNos } = action.payload;
      
      if (!state.requestsRef.length) return;
      
      if (country === "JDWIN") {
        state.requests = state.requestsRef.filter((res) =>
          extensionType
            ? res?.country === countryCode && res?.extensionType === extensionType
            : !otherThanJDWINCountries.includes(res?.country)
        );
      } else if (country && countryCode) {
        state.requests = state.requestsRef.filter((res) =>
          extensionType
            ? res?.country === countryCode && res?.extensionType === extensionType
            : res?.country === countryCode
        );
      } else if (extensionType) {
        state.requests = state.requestsRef.filter((res) =>
          res?.extensionType === extensionType
        );
      } else {
        state.requests = state.requestsRef;
      }
      if (referenceContract) {
  state.requests = state.requests.filter((res) =>
    String(res?.referenceContract ?? "").toLowerCase().includes(String(referenceContract).toLowerCase())
  );
}
      if (serialNos && serialNos.length > 0) {
        state.requests = state.requests.filter((res) => {
          const rowSerial = String(res?.serialNo ?? "").toLowerCase();
          return serialNos.some((sn) => rowSerial.includes(String(sn).toLowerCase()));
        });
      }
    },
    clearCCMError: (state) => {
      state.error = null;
    },
    clearCCMActionError: (state) => {
      state.actionError = null;
    },
    resetCCMExtension: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCCMRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCCMRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload;
        state.requestsRef = action.payload;
      })
      .addCase(fetchCCMRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(sendToBP.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(sendToBP.fulfilled, (state, action) => {
        state.actionLoading = false;
        const id = action.payload;
        state.requests = state.requests.filter((req) => req.id !== id);
        state.requestsRef = state.requestsRef.filter((req) => req.id !== id);
      })
      .addCase(sendToBP.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });

    builder
      .addCase(saveCCMData.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(saveCCMData.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(saveCCMData.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });
  },
});

export const {
  filterCCMRequests,
  clearCCMError,
  clearCCMActionError,
  resetCCMExtension,
} = ccmExtensionSlice.actions;

export default ccmExtensionSlice.reducer;
